import type { BuildingFamily } from "@stahlfront/core";

/**
 * §5 base scene layout — 11 building plots on the 14×14 ground plate.
 * The base scene is organised front-to-back into three thematic bands:
 *   row 1 (z ≈ -4.0, "military front"):  GROUND_FORCES_BASE / BARRACKS / AA_BASE / AIR_FORCE_BASE
 *   row 2 (z ≈ -1.0, "command middle"):  OFFICER_HQ / MILITARY_CENTER / RESEARCH_CENTER / VEHICLE_HANGARS
 *   row 3 (z ≈ +2.5, "industry back"):   ARMORY / RESOURCE_FACTORY / SALVAGE_YARD
 * Columns sit at x = -4.5, -1.5, +1.5, +4.5 (one cell empty in row 3 right).
 *
 * Each entry is `[x, z, rotationY]`. Rotations are deliberately staggered by
 * small ±10° offsets to break visual repetition without breaking grid clarity.
 * Camera lives ~14 units up at the BASE zoom layer; meshes peak at y≈3, well
 * inside the framing rectangle.
 */
export type BuildingTransform = readonly [x: number, z: number, rotationY: number];

export const BUILDING_LAYOUT: Readonly<Record<BuildingFamily, BuildingTransform>> = {
  // Row 1 — military front (z = -4)
  GROUND_FORCES_BASE: [-4.5, -4.0, 0.18],
  BARRACKS: [-1.5, -4.0, -0.12],
  AA_BASE: [1.5, -4.0, 0.06],
  AIR_FORCE_BASE: [4.5, -4.0, -0.16],
  // Row 2 — command middle (z = -1)
  OFFICER_HQ: [-4.5, -1.0, -0.08],
  MILITARY_CENTER: [-1.5, -1.0, 0.14],
  RESEARCH_CENTER: [1.5, -1.0, -0.05],
  VEHICLE_HANGARS: [4.5, -1.0, 0.0],
  // Row 3 — industry back (z = +2.5)
  ARMORY: [-4.5, 2.5, 0.1],
  RESOURCE_FACTORY: [-1.5, 2.5, -0.18],
  SALVAGE_YARD: [1.5, 2.5, 0.0],
};

/** Approximate footprint radius of a single building cluster, in world units.
 *  Used by raycast-helper sizing + accessible aria-label "X-by-Y compound". */
export const BUILDING_FOOTPRINT = 2.4 as const;
